/**
 * GameGuidePage - Comprehensive game guide for Centauri simulation
 * Full documentation of all game mechanics, stages, scoring, and outcomes
 */

import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { QuestionCard } from '@/components/guide/QuestionCard';
import { NarrativeBlock } from '@/components/guide/NarrativeBlock';
import { EnergyTable } from '@/components/guide/EnergyTable';
import { RoleMessageTabs } from '@/components/guide/RoleMessageTabs';
import {
  QUESTIONS,
  STAGE_CONTENT,
  ENERGY_CHANGES,
  ENDINGS,
  GAME_CONFIG,
  MISSION_STAGES_SUMMARY,
} from '@/data/gameGuideContent';
import { VideoPlayer } from '@/game/components/VideoPlayer';
import { STARTUP_VIDEO } from '@/game/content/videoConfig';
import {
  HiOutlineClipboardList,
  HiOutlineFlag,
  HiOutlineUserGroup,
  HiOutlineStar,
  HiOutlineQuestionMarkCircle,
  HiOutlineLightningBolt,
  HiOutlineCube,
  HiOutlineExclamationCircle,
  HiOutlineCheckCircle,
  HiOutlineUsers,
} from 'react-icons/hi';
import {
  FaBrain,
  FaHeart,
  FaComments,
  FaBalanceScale,
  FaTrophy,
  FaSkullCrossbones,
  FaNewspaper,
} from 'react-icons/fa';

type GuideSection = 'overview' | 'stages' | 'characters' | 'scoring' | 'questions' | 'endings';

export function GameGuidePage() {
  const [activeSection, setActiveSection] = useState<GuideSection>('overview');
  const guideTabTriggerClass =
    'flex items-center gap-2 h-10 px-3 rounded-lg bg-transparent text-slate-600 data-[state=active]:bg-white data-[state=active]:text-[#12356B] data-[state=active]:shadow-sm hover:text-[#12356B] transition-colors';

  return (
    <div className="min-h-screen bg-[#E2E5EC]/50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#12356B] mb-2">Centauri Game Guide</h1>
          <p className="text-slate-600">
            Comprehensive reference for game mechanics, stages, scoring, and outcomes
          </p>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeSection} onValueChange={(value) => setActiveSection(value as GuideSection)}>
          <TabsList className="mb-6 w-full flex-wrap h-auto bg-[#E2E5EC] p-1 rounded-xl">
            <TabsTrigger value="overview" className={guideTabTriggerClass}>
              <HiOutlineClipboardList className="text-lg" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="stages" className={guideTabTriggerClass}>
              <HiOutlineFlag className="text-lg" />
              <span>Stage Progression</span>
            </TabsTrigger>
            <TabsTrigger value="characters" className={guideTabTriggerClass}>
              <HiOutlineUserGroup className="text-lg" />
              <span>Characters & Roles</span>
            </TabsTrigger>
            <TabsTrigger value="scoring" className={guideTabTriggerClass}>
              <HiOutlineStar className="text-lg" />
              <span>Scoring System</span>
            </TabsTrigger>
            <TabsTrigger value="questions" className={guideTabTriggerClass}>
              <HiOutlineQuestionMarkCircle className="text-lg" />
              <span>Questions & Decisions</span>
            </TabsTrigger>
            <TabsTrigger value="endings" className={guideTabTriggerClass}>
              <FaTrophy className="text-lg" />
              <span>Endings & Outcomes</span>
            </TabsTrigger>
          </TabsList>

          {/* SECTION 1: OVERVIEW */}
          <TabsContent value="overview">
            <div className="space-y-8">
              {/* Game Explainer Video */}
              <div>
                <h3 className="text-xl font-bold text-[#12356B] mb-4">How to Play Centauri</h3>
                <div className="max-w-3xl">
                  <VideoPlayer
                    src={STARTUP_VIDEO}
                    onEnded={() => {}}
                    autoPlay={false}
                  />
                </div>
              </div>

              {/* Mission Summary Banner */}
              <div className="bg-gradient-to-r from-[#12356B] to-[#0d2850] text-white rounded-lg p-8 shadow-lg">
                <h2 className="text-2xl font-bold mb-4">Mission: Capture the Rainbow Ray</h2>
                <p className="text-lg leading-relaxed mb-4">
                  A team of five Centaurians must descend to the aquatic planet Bloomtar to capture a live
                  Rainbow Ray for DNA extraction. The Ray's ability to teleport is crucial for interstellar
                  travel. The team has a 6-day hunting permit and must manage their energy, resources, and
                  teamwork to complete the mission successfully.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold">{GAME_CONFIG.playerCount.max}</div>
                    <div className="text-sm opacity-90 mt-1">Players</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold">6</div>
                    <div className="text-sm opacity-90 mt-1">Stages</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold">14+</div>
                    <div className="text-sm opacity-90 mt-1">Questions</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold">4</div>
                    <div className="text-sm opacity-90 mt-1">Score Dimensions</div>
                  </div>
                </div>
              </div>

              {/* Key Mechanics */}
              <div>
                <h3 className="text-xl font-bold text-[#12356B] mb-4">Key Mechanics</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Energy Management */}
                  <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#DD6832]/10 rounded-lg">
                        <HiOutlineLightningBolt className="text-2xl text-[#c4562a]" />
                      </div>
                      <h4 className="text-lg font-bold text-[#12356B]">Energy Management</h4>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mb-3">
                      Each player starts with 65-100% energy. The planet depletes energy each day, and players
                      must consume energy meals to stay alive. Energy drops to 0% means death.
                    </p>
                    <ul className="text-sm text-slate-700 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-[#c4562a]">•</span>
                        <span>Initial: {GAME_CONFIG.energySystem.initialRange.min}-{GAME_CONFIG.energySystem.initialRange.max}%</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#c4562a]">•</span>
                        <span>Energy packs restore +{GAME_CONFIG.energySystem.energyPackValue}%</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#c4562a]">•</span>
                        <span>Total packs: {GAME_CONFIG.energySystem.initialEnergyPacks}</span>
                      </li>
                    </ul>
                  </div>

                  {/* Inventory & Equipment */}
                  <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#12356B]/10 rounded-lg">
                        <HiOutlineCube className="text-2xl text-[#12356B]" />
                      </div>
                      <h4 className="text-lg font-bold text-[#12356B]">Inventory & Equipment</h4>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mb-3">
                      The team starts with basic equipment and must choose an equipment tub (A, B, or C) at the
                      beginning. Each tub contains different items that affect gameplay.
                    </p>
                    <ul className="text-sm text-slate-700 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-[#12356B]">•</span>
                        <span>Diving Bell (Articus II)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#12356B]">•</span>
                        <span>Decompression Chamber</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#12356B]">•</span>
                        <span>Bio Transport Capsules</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#12356B]">•</span>
                        <span>Nuclear Fuel Canisters</span>
                      </li>
                    </ul>
                  </div>

                  {/* Branching Narrative */}
                  <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#12356B]/10 rounded-lg">
                        <HiOutlineExclamationCircle className="text-2xl text-[#12356B]" />
                      </div>
                      <h4 className="text-lg font-bold text-[#12356B]">Branching Narrative</h4>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mb-3">
                      Player decisions affect the story, character messages, and available options. Different
                      roles receive different information and perspectives.
                    </p>
                    <ul className="text-sm text-slate-700 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-[#12356B]">•</span>
                        <span>Role-specific messages</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#12356B]">•</span>
                        <span>Conditional story branches</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#12356B]">•</span>
                        <span>Multiple endings (7+)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Mission Timeline */}
              <div>
                <h3 className="text-xl font-bold text-[#12356B] mb-4">Mission Timeline</h3>
                <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                  <div className="space-y-4">
                    {MISSION_STAGES_SUMMARY.map((stage) => (
                      <div key={stage.stage} className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-[#12356B]/10 text-[#12356B] rounded-full flex items-center justify-center font-bold">
                            {stage.stage}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-base font-bold text-[#12356B] mb-1">{stage.title}</h4>
                          <p className="text-sm text-slate-700 leading-relaxed">{stage.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Scoring Dimensions */}
              <div>
                <h3 className="text-xl font-bold text-[#12356B] mb-4">Leadership Style Dimensions</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <FaBrain className="text-3xl text-[#12356B]" />
                      <div>
                        <h4 className="text-lg font-bold text-[#12356B]">Brain (Analytical)</h4>
                        <p className="text-xs text-slate-600">Logic, strategy, and analytical thinking</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Measures logical decision-making, strategic planning, and analytical problem-solving.
                      High Brain scores indicate leaders who prioritize data and reasoning.
                    </p>
                  </div>

                  <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <FaHeart className="text-3xl text-[#c4562a]" />
                      <div>
                        <h4 className="text-lg font-bold text-[#12356B]">Heart (Empathy)</h4>
                        <p className="text-xs text-slate-600">Emotion, care, and team welfare</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Measures empathy, emotional intelligence, and concern for team welfare. High Heart
                      scores indicate leaders who prioritize people and relationships.
                    </p>
                  </div>

                  <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <FaBalanceScale className="text-3xl text-[#12356B]" />
                      <div>
                        <h4 className="text-lg font-bold text-[#12356B]">Task (Achievement)</h4>
                        <p className="text-xs text-slate-600">Mission focus and goal achievement</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Measures task orientation, mission focus, and goal achievement. High Task scores
                      indicate leaders who prioritize objectives and results.
                    </p>
                  </div>

                  <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <FaComments className="text-3xl text-[#367A25]" />
                      <div>
                        <h4 className="text-lg font-bold text-[#12356B]">Comm (Communication)</h4>
                        <p className="text-xs text-slate-600">Collaboration and information sharing</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Measures communication effectiveness, collaboration, and information sharing. High Comm
                      scores indicate leaders who prioritize teamwork and coordination.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* SECTION 2: STAGE PROGRESSION */}
          <TabsContent value="stages">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-[#12356B]">Stage-by-Stage Progression</h2>
              <p className="text-slate-600 mb-6">
                Detailed breakdown of all game stages, including narratives, character messages, questions,
                energy impacts, and objectives.
              </p>

              <Accordion type="single" collapsible className="space-y-4">
                {STAGE_CONTENT.map((stage) => {
                  const stageQuestions = QUESTIONS.filter((q) => q.stage === stage.stage);
                  const stageEnergy = ENERGY_CHANGES[stage.stage] || [];

                  return (
                    <AccordionItem
                      key={stage.stage}
                      value={`stage-${stage.stage}`}
                      className="bg-white border border-[#CED4DE] rounded-lg overflow-hidden"
                    >
                      <AccordionTrigger className="px-6 hover:bg-[#E2E5EC]/50">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-12 h-12 bg-[#12356B] text-white rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0">
                            {stage.stage}
                          </div>
                          <div className="text-left flex-1">
                            <div className="text-lg font-bold text-[#12356B]">{stage.title}</div>
                            <div className="text-sm text-slate-600">{stage.dayOfPermit}</div>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="px-6">
                        {/* Opening Narrative */}
                        <div className="mb-8">
                          <h4 className="text-base font-bold text-[#12356B] mb-4 flex items-center gap-2">
                            <HiOutlineClipboardList className="text-lg" />
                            Opening Narrative
                          </h4>
                          <div className="space-y-4">
                            {stage.narrative.map((text, idx) => (
                              <NarrativeBlock key={idx} speaker="NARRATOR" content={text} />
                            ))}
                          </div>
                        </div>

                        {/* Character Messages */}
                        {Object.keys(stage.characterMessages).length > 0 && (
                          <div className="mb-8">
                            <h4 className="text-base font-bold text-[#12356B] mb-4 flex items-center gap-2">
                              <HiOutlineUsers className="text-lg" />
                              Character Messages
                            </h4>
                            <RoleMessageTabs
                              roleMessages={Object.entries(stage.characterMessages).map(([role, messages]) => ({
                                role,
                                messages: messages.map((msg) => ({
                                  source: msg.icon?.toUpperCase() || 'MESSAGE',
                                  text: msg.message,
                                  icon: msg.icon,
                                })),
                              }))}
                            />
                          </div>
                        )}

                        {/* Questions */}
                        {stageQuestions.length > 0 && (
                          <div className="mb-8">
                            <h4 className="text-base font-bold text-[#12356B] mb-4 flex items-center gap-2">
                              <HiOutlineQuestionMarkCircle className="text-lg" />
                              Decision Points
                            </h4>
                            {stageQuestions.map((question) => (
                              <QuestionCard
                                key={question.id}
                                questionNumber={question.id}
                                questionText={question.text}
                                decisionType={
                                  question.type === 'text'
                                    ? 'Individual'
                                    : question.id === 'Q6'
                                    ? 'Consensus'
                                    : question.id === 'Q8'
                                    ? 'Majority Vote'
                                    : 'Individual'
                                }
                                answers={question.choices.map((choice) => ({
                                  choice: choice.id,
                                  text: choice.text,
                                  scoring: {
                                    brain: choice.scores.brain,
                                    heart: choice.scores.heart,
                                    task: choice.scores.task,
                                    comm: choice.scores.comm,
                                  },
                                }))}
                                context={question.notes}
                                conditionalLogic={
                                  question.id.includes('A') || question.id.includes('B')
                                    ? question.notes
                                    : undefined
                                }
                              />
                            ))}
                          </div>
                        )}

                        {/* Energy Impacts */}
                        {stageEnergy.length > 0 && (
                          <div className="mb-8">
                            <h4 className="text-base font-bold text-[#12356B] mb-4 flex items-center gap-2">
                              <HiOutlineLightningBolt className="text-lg text-[#c4562a]" />
                              Energy Impacts
                            </h4>
                            <EnergyTable
                              changes={stageEnergy.map((change) => ({
                                role: change.character,
                                condition: change.condition || 'All',
                                change: change.change > 0 ? `+${change.change}%` : `${change.change}%`,
                                description: change.message,
                              }))}
                            />
                          </div>
                        )}

                        {/* Stage Objectives */}
                        <div className="mb-4">
                          <h4 className="text-base font-bold text-[#12356B] mb-4 flex items-center gap-2">
                            <HiOutlineCheckCircle className="text-lg text-[#367A25]" />
                            Stage Objectives
                          </h4>
                          <ul className="space-y-2">
                            {stage.objectives.map((objective, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-sm text-slate-700">
                                <HiOutlineCheckCircle className="text-[#367A25] mt-0.5 flex-shrink-0" />
                                <span>{objective}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Special Notes for Stage 2 */}
                        {stage.stage === 2 && (
                          <div className="mt-6 p-4 bg-[#DD6832]/10 border border-[#DD6832]/30 rounded-lg">
                            <div className="flex items-start gap-3">
                              <HiOutlineExclamationCircle className="text-[#c4562a] text-xl mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-sm font-bold text-[#c4562a] mb-2">
                                  Cave of Noorah - Special Mechanics
                                </div>
                                <ul className="text-sm text-[#c4562a] space-y-1">
                                  <li>• Cave exploration: +30% energy (healing power)</li>
                                  <li>• Ship exploration: -20% energy (Diamond Bugs)</li>
                                  <li>• DIVER ship exploration: -30% (poisoned by Diamond Bugs)</li>
                                  <li>• FIXER has unique Q7B choices (can examine Diving Bell)</li>
                                  <li>• FIXER cave choice (Q7B = A): -2 to all scores (triggers Stage 5A)</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Special Notes for Stage 5 */}
                        {stage.stage === 5 && (
                          <div className="mt-6 p-4 bg-[#DD6832]/10 border border-[#DD6832]/30 rounded-lg">
                            <div className="flex items-start gap-3">
                              <HiOutlineExclamationCircle className="text-[#c4562a] text-xl mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-sm font-bold text-[#c4562a] mb-2">
                                  Emergency Ascent - Two Variants
                                </div>
                                <div className="space-y-3">
                                  <div>
                                    <div className="text-sm font-semibold text-[#c4562a]">
                                      Stage 5A (if FIXER Q7 = A or C)
                                    </div>
                                    <p className="text-sm text-[#c4562a] mt-1">
                                      All players ascend together. Questions: Q10A, Q12A, Q13A, Q14A
                                    </p>
                                  </div>
                                  <div>
                                    <div className="text-sm font-semibold text-[#c4562a]">
                                      Stage 5B (if FIXER Q7 = B)
                                    </div>
                                    <p className="text-sm text-[#c4562a] mt-1">
                                      Only 3 players fit on Diving Bell. Others must swim. Questions: Q10B, Q11B
                                      (swimmers only), Q12B (swimmers only), Q13B, Q14B
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </TabsContent>

          {/* SECTION 3: CHARACTERS & ROLES */}
          <TabsContent value="characters">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-[#12356B]">Characters & Roles</h2>
              <p className="text-slate-600 mb-6">
                Five playable roles and two NPCs, each with unique perspectives, abilities, and information.
              </p>

              {/* Playable Characters */}
              <div>
                <h3 className="text-xl font-bold text-[#12356B] mb-4">Playable Characters</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* BIOLOGIST */}
                  <div className="bg-white border-2 border-[#367A25]/25 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#367A25]/10 rounded-lg">
                        <img src="/icons/biologist.png" alt="BIOLOGIST" className="w-10 h-10 object-contain" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-[#367A25]">BIOLOGIST</h4>
                        <p className="text-xs text-slate-600">Life & Species Expert</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mb-4">
                      Specializes in Bloomtar's species and biology. Can identify creatures like the Golden
                      Octopus. Receives messages about Bio Transport Capsules and creature capture requirements.
                    </p>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-[#12356B]">Key Information:</div>
                      <ul className="text-xs text-slate-700 space-y-1">
                        <li>• Spots Golden Octopus in Stage 2</li>
                        <li>• Knows capture requires 2 people</li>
                        <li>• Limited Bio Transport Capsules</li>
                        <li>• Receives Fire Flower requests from home</li>
                      </ul>
                    </div>
                  </div>

                  {/* DIVER */}
                  <div className="bg-white border-2 border-[#12356B]/25 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#12356B]/10 rounded-lg">
                        <img src="/icons/diver.png" alt="DIVER" className="w-10 h-10 object-contain" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-[#12356B]">DIVER</h4>
                        <p className="text-xs text-slate-600">Environmental Specialist</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mb-4">
                      Specializes in environmental assessment and diving safety. Secondary mission is to assess
                      and clean pollution. Gets poisoned by Diamond Bugs if exploring sunken ship.
                    </p>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-[#12356B]">Key Information:</div>
                      <ul className="text-xs text-slate-700 space-y-1">
                        <li>• Primary: Capture Rainbow Ray</li>
                        <li>• Secondary: Clean pollution</li>
                        <li>• Needs 3+ people for full cleanup</li>
                        <li>• -30% energy if ship exploration (Stage 2)</li>
                      </ul>
                    </div>
                  </div>

                  {/* FIXER */}
                  <div className="bg-white border-2 border-[#DD6832]/30 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#DD6832]/10 rounded-lg">
                        <img src="/icons/fixer.png" alt="FIXER" className="w-10 h-10 object-contain" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-[#c4562a]">FIXER</h4>
                        <p className="text-xs text-slate-600">Technical Specialist</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mb-4">
                      Specializes in Diving Bell maintenance. Has unique Stage 2 question (Q7B). Their choice
                      determines Stage 5 variant (5A or 5B). Injured in Stage 1 (-25% energy). Remembers
                      previous mission disaster.
                    </p>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-[#12356B]">Key Information:</div>
                      <ul className="text-xs text-slate-700 space-y-1">
                        <li>• Only needs 3 fuel canisters</li>
                        <li>• Q7 choice determines Stage 5 variant</li>
                        <li>• Can rig Bell to filter toxins</li>
                        <li>• Injured in Stage 1 (-25% energy)</li>
                      </ul>
                    </div>
                  </div>

                  {/* HISTORIAN */}
                  <div className="bg-white border-2 border-[#12356B]/30 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#12356B]/10 rounded-lg">
                        <img src="/icons/historian.png" alt="HISTORIAN" className="w-10 h-10 object-contain" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-[#12356B]">HISTORIAN</h4>
                        <p className="text-xs text-slate-600">Captain Ahab Expert</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mb-4">
                      Specializes in Captain Ahab's history and hieroglyphs. Needs Translation Device to read
                      ancient markings. Can spot Ahab's Platinum Journal on ocean floor. Promotion to Galactic
                      Expert if they surface with journal.
                    </p>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-[#12356B]">Key Information:</div>
                      <ul className="text-xs text-slate-700 space-y-1">
                        <li>• Needs Translation Device</li>
                        <li>• Spots Platinum Journal in Stage 4</li>
                        <li>• Promotion if journal surfaced</li>
                        <li>• Warning: "[INSERT CODE] means DEATH"</li>
                      </ul>
                    </div>
                  </div>

                  {/* HUNTER */}
                  <div className="bg-white border-2 border-[#DD6832]/30 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#DD6832]/10 rounded-lg">
                        <img src="/icons/hunter.png" alt="HUNTER" className="w-10 h-10 object-contain" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-[#c4562a]">HUNTER</h4>
                        <p className="text-xs text-slate-600">Tracker & Permit Enforcer</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mb-4">
                      Specializes in tracking and hunting permit enforcement. Secondary mission is to find the
                      Black Box from the previous mission. Can track Photon Trails. Remembers being on the
                      Articus I disaster mission.
                    </p>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-[#12356B]">Key Information:</div>
                      <ul className="text-xs text-slate-700 space-y-1">
                        <li>• Enforces 6-day hunting permit</li>
                        <li>• Must find Black Box</li>
                        <li>• Spots Black Box in Stage 4</li>
                        <li>• Was on previous Articus I mission</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* NPCs */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-[#12356B] mb-4">Non-Playable Characters (NPCs)</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* DOC */}
                  <div className="bg-white border-2 border-[#367A25]/25 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#367A25]/10 rounded-lg">
                        <img src="/icons/doc.png" alt="DOC" className="w-8 h-8 object-contain" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-[#367A25]">DOC</h4>
                        <p className="text-xs text-slate-600">Healthcare Expert</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mb-4">
                      Monitors team health and energy levels. Provides medical advice and warnings about energy
                      consumption. Stays on Diving Bell in Stage 4 to fight Diamond Bugs.
                    </p>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-[#12356B]">Key Responsibilities:</div>
                      <ul className="text-xs text-slate-700 space-y-1">
                        <li>• Monitor energy levels</li>
                        <li>• Provide health warnings</li>
                        <li>• Decompression safety expert</li>
                        <li>• Fights Diamond Bugs in Stage 4</li>
                      </ul>
                    </div>
                  </div>

                  {/* GEE */}
                  <div className="bg-white border-2 border-[#DD6832]/30 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-[#DD6832]/10 rounded-lg">
                        <img src="/icons/GEE.png" alt="GEE" className="w-8 h-8 object-contain" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-[#c4562a]">GEE</h4>
                        <p className="text-xs text-slate-600">Galactic Expedition Expert</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mb-4">
                      Veteran expedition member with mission experience. Secured the 6-day hunting permit. Loses
                      equipment tub in Stage 1 storm. Disappears on adventure in Stage 4. First to make energy
                      choice in Stage 5A.
                    </p>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-[#12356B]">Key Characteristics:</div>
                      <ul className="text-xs text-slate-700 space-y-1">
                        <li>• Secured hunting permit</li>
                        <li>• Can transport back to Mothership</li>
                        <li>• Loses tub in Stage 1</li>
                        <li>• Leaves audio note: "Don't leave debris"</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Character Message Timeline */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-[#12356B] mb-4">Character Message Timeline</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Each character receives unique messages throughout the game. Expand each stage below to see
                  all character messages.
                </p>

                <Accordion type="single" collapsible className="space-y-4">
                  {STAGE_CONTENT.map((stage) => {
                    const hasMessages = Object.keys(stage.characterMessages).length > 0;
                    if (!hasMessages) return null;

                    return (
                      <AccordionItem
                        key={`char-stage-${stage.stage}`}
                        value={`char-stage-${stage.stage}`}
                        className="bg-white border border-[#CED4DE] rounded-lg overflow-hidden"
                      >
                        <AccordionTrigger className="px-6 hover:bg-[#E2E5EC]/50">
                          <div className="flex items-center gap-4">
                            <div className="text-base font-bold text-[#12356B]">
                              Stage {stage.stage}: {stage.title}
                            </div>
                          </div>
                        </AccordionTrigger>

                        <AccordionContent className="px-6">
                          <RoleMessageTabs
                            roleMessages={Object.entries(stage.characterMessages).map(([role, messages]) => ({
                              role,
                              messages: messages.map((msg) => ({
                                source: msg.icon?.toUpperCase() || 'MESSAGE',
                                text: msg.message,
                                icon: msg.icon,
                              })),
                            }))}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            </div>
          </TabsContent>

          {/* SECTION 4: SCORING SYSTEM */}
          <TabsContent value="scoring">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-[#12356B]">Scoring System</h2>
              <p className="text-slate-600 mb-6">
                Complete scoring breakdown for all questions and answer choices. Scores accumulate across the
                game to determine leadership style distribution.
              </p>

              {/* Scoring Legend */}
              <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm mb-6">
                <h3 className="text-lg font-bold text-[#12356B] mb-4">Scoring Dimensions</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-[#12356B]/10 border border-[#12356B]/25 rounded-lg">
                    <FaBrain className="text-2xl text-[#12356B]" />
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Brain</div>
                      <div className="text-xs text-slate-600">Analytical</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#DD6832]/10 border border-[#DD6832]/30 rounded-lg">
                    <FaHeart className="text-2xl text-[#c4562a]" />
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Heart</div>
                      <div className="text-xs text-slate-600">Empathy</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#E2E5EC] border border-[#CED4DE] rounded-lg">
                    <FaBalanceScale className="text-2xl text-[#12356B]" />
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Task</div>
                      <div className="text-xs text-slate-600">Achievement</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#367A25]/10 border border-[#367A25]/25 rounded-lg">
                    <FaComments className="text-2xl text-[#367A25]" />
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Comm</div>
                      <div className="text-xs text-slate-600">Communication</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Question Scoring by Stage */}
              <Accordion type="single" collapsible className="space-y-4">
                {[0, 1, 2, 3, 4, 5].map((stageNum) => {
                  const stageQuestions = QUESTIONS.filter((q) => q.stage === stageNum);
                  if (stageQuestions.length === 0) return null;

                  return (
                    <AccordionItem
                      key={`score-stage-${stageNum}`}
                      value={`score-stage-${stageNum}`}
                      className="bg-white border border-[#CED4DE] rounded-lg overflow-hidden"
                    >
                      <AccordionTrigger className="px-6 hover:bg-[#E2E5EC]/50">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#12356B] text-white rounded-lg flex items-center justify-center font-bold">
                            {stageNum}
                          </div>
                          <div className="text-left">
                            <div className="text-base font-bold text-[#12356B]">
                              Stage {stageNum} Scoring
                            </div>
                            <div className="text-sm text-slate-600">
                              {stageQuestions.length} question{stageQuestions.length > 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="px-6">
                        <div className="space-y-6">
                          {stageQuestions.map((question) => {
                            // Skip text-only questions
                            if (question.choices.length === 0) {
                              return (
                                <div key={question.id} className="border border-[#CED4DE] rounded-lg p-4 bg-[#E2E5EC]/50">
                                  <div className="text-sm font-bold text-[#12356B] mb-2">{question.id}</div>
                                  <p className="text-sm text-slate-700 mb-2">{question.text}</p>
                                  <div className="text-xs text-slate-600 italic">
                                    {question.notes || 'Open text response - no scoring'}
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div key={question.id} className="border border-[#CED4DE] rounded-lg p-4 bg-white">
                                <div className="mb-4">
                                  <div className="text-sm font-bold text-[#12356B] mb-2">{question.id}</div>
                                  <p className="text-sm text-slate-700 mb-2">{question.text}</p>
                                  {question.notes && (
                                    <div className="text-xs text-slate-600 italic">{question.notes}</div>
                                  )}
                                </div>

                                {/* Scoring Table */}
                                <div className="overflow-x-auto">
                                  <table className="min-w-full text-xs">
                                    <thead className="bg-[#E2E5EC]/50 border-b border-[#CED4DE]">
                                      <tr>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                          Choice
                                        </th>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                          Answer
                                        </th>
                                        <th className="px-3 py-2 text-center font-semibold text-[#12356B]">
                                          <div className="flex items-center justify-center gap-1">
                                            <FaBrain />
                                            <span>Brain</span>
                                          </div>
                                        </th>
                                        <th className="px-3 py-2 text-center font-semibold text-[#c4562a]">
                                          <div className="flex items-center justify-center gap-1">
                                            <FaHeart />
                                            <span>Heart</span>
                                          </div>
                                        </th>
                                        <th className="px-3 py-2 text-center font-semibold text-[#12356B]">
                                          <div className="flex items-center justify-center gap-1">
                                            <FaBalanceScale />
                                            <span>Task</span>
                                          </div>
                                        </th>
                                        <th className="px-3 py-2 text-center font-semibold text-[#367A25]">
                                          <div className="flex items-center justify-center gap-1">
                                            <FaComments />
                                            <span>Comm</span>
                                          </div>
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {question.choices.map((choice, idx) => (
                                        <tr
                                          key={choice.id}
                                          className={idx % 2 === 0 ? 'bg-[#E2E5EC]/50' : 'bg-white'}
                                        >
                                          <td className="px-3 py-2 font-bold text-[#12356B]">
                                            {choice.id}
                                          </td>
                                          <td className="px-3 py-2 text-slate-700">
                                            {choice.text.substring(0, 80)}
                                            {choice.text.length > 80 ? '...' : ''}
                                          </td>
                                          <td className="px-3 py-2 text-center font-semibold">
                                            <span
                                              className={
                                                choice.scores.brain > 0
                                                  ? 'text-[#367A25]'
                                                  : choice.scores.brain < 0
                                                  ? 'text-[#c4562a]'
                                                  : 'text-slate-600'
                                              }
                                            >
                                              {choice.scores.brain > 0 ? '+' : ''}
                                              {choice.scores.brain}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-center font-semibold">
                                            <span
                                              className={
                                                choice.scores.heart > 0
                                                  ? 'text-[#367A25]'
                                                  : choice.scores.heart < 0
                                                  ? 'text-[#c4562a]'
                                                  : 'text-slate-600'
                                              }
                                            >
                                              {choice.scores.heart > 0 ? '+' : ''}
                                              {choice.scores.heart}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-center font-semibold">
                                            <span
                                              className={
                                                choice.scores.task > 0
                                                  ? 'text-[#367A25]'
                                                  : choice.scores.task < 0
                                                  ? 'text-[#c4562a]'
                                                  : 'text-slate-600'
                                              }
                                            >
                                              {choice.scores.task > 0 ? '+' : ''}
                                              {choice.scores.task}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-center font-semibold">
                                            <span
                                              className={
                                                choice.scores.comm > 0
                                                  ? 'text-[#367A25]'
                                                  : choice.scores.comm < 0
                                                  ? 'text-[#c4562a]'
                                                  : 'text-slate-600'
                                              }
                                            >
                                              {choice.scores.comm > 0 ? '+' : ''}
                                              {choice.scores.comm}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              {/* Leadership Style Examples */}
              <div className="mt-8 bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-bold text-[#12356B] mb-4">
                  Leadership Style Distribution Examples
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Final scores determine each player's leadership style profile. High scores in a dimension
                  indicate a preference for that leadership approach.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border border-[#12356B]/25 rounded-lg p-4 bg-[#12356B]/10">
                    <div className="text-sm font-bold text-[#12356B] mb-3">
                      Example: Analytical Leader
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Brain:</span>
                        <span className="font-bold text-[#12356B]">+18</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Heart:</span>
                        <span className="font-bold text-slate-600">+4</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Task:</span>
                        <span className="font-bold text-slate-600">+8</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Comm:</span>
                        <span className="font-bold text-slate-600">+6</span>
                      </div>
                    </div>
                  </div>

                  <div className="border border-[#DD6832]/30 rounded-lg p-4 bg-[#DD6832]/10">
                    <div className="text-sm font-bold text-[#12356B] mb-3">
                      Example: Empathetic Leader
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Brain:</span>
                        <span className="font-bold text-slate-600">+5</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Heart:</span>
                        <span className="font-bold text-[#c4562a]">+16</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Task:</span>
                        <span className="font-bold text-slate-600">+2</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Comm:</span>
                        <span className="font-bold text-slate-600">+7</span>
                      </div>
                    </div>
                  </div>

                  <div className="border border-[#CED4DE] rounded-lg p-4 bg-[#E2E5EC]">
                    <div className="text-sm font-bold text-[#12356B] mb-3">
                      Example: Task-Oriented Leader
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Brain:</span>
                        <span className="font-bold text-slate-600">+7</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Heart:</span>
                        <span className="font-bold text-slate-600">+3</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Task:</span>
                        <span className="font-bold text-[#12356B]">+19</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Comm:</span>
                        <span className="font-bold text-slate-600">+8</span>
                      </div>
                    </div>
                  </div>

                  <div className="border border-[#367A25]/25 rounded-lg p-4 bg-[#367A25]/10">
                    <div className="text-sm font-bold text-[#12356B] mb-3">
                      Example: Collaborative Leader
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Brain:</span>
                        <span className="font-bold text-slate-600">+6</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Heart:</span>
                        <span className="font-bold text-slate-600">+8</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Task:</span>
                        <span className="font-bold text-slate-600">+7</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">Comm:</span>
                        <span className="font-bold text-[#367A25]">+17</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* SECTION 5: QUESTIONS & DECISIONS */}
          <TabsContent value="questions">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-[#12356B]">Questions & Decisions</h2>
              <p className="text-slate-600 mb-6">
                Complete library of all questions, answer choices, scoring impacts, energy costs, and
                consequences. Questions are organized by stage.
              </p>

              {/* Decision Type Legend */}
              <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm mb-6">
                <h3 className="text-lg font-bold text-[#12356B] mb-4">Decision Types</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-[#12356B] rounded-full"></div>
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Individual</div>
                      <div className="text-xs text-slate-600">Each player chooses independently</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-[#12356B] rounded-full"></div>
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Majority Vote</div>
                      <div className="text-xs text-slate-600">Most common choice wins</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-[#367A25] rounded-full"></div>
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Consensus</div>
                      <div className="text-xs text-slate-600">Team must agree together</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-[#DD6832] rounded-full"></div>
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Unanimous</div>
                      <div className="text-xs text-slate-600">All players must choose same</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-[#DD6832] rounded-full"></div>
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Isolated</div>
                      <div className="text-xs text-slate-600">Player chooses alone, no discussion</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-[#DD6832] rounded-full"></div>
                    <div>
                      <div className="text-sm font-bold text-[#12356B]">Conditional</div>
                      <div className="text-xs text-slate-600">Only shown to specific roles/conditions</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Questions by Stage */}
              <Accordion type="single" collapsible className="space-y-4">
                {[0, 1, 2, 3, 4, 5].map((stageNum) => {
                  const stageQuestions = QUESTIONS.filter((q) => q.stage === stageNum);
                  if (stageQuestions.length === 0) return null;

                  return (
                    <AccordionItem
                      key={`questions-stage-${stageNum}`}
                      value={`questions-stage-${stageNum}`}
                      className="bg-white border border-[#CED4DE] rounded-lg overflow-hidden"
                    >
                      <AccordionTrigger className="px-6 hover:bg-[#E2E5EC]/50">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#12356B] text-white rounded-lg flex items-center justify-center font-bold">
                            {stageNum}
                          </div>
                          <div className="text-left">
                            <div className="text-base font-bold text-[#12356B]">
                              Stage {stageNum} Questions
                            </div>
                            <div className="text-sm text-slate-600">
                              {stageQuestions.length} question{stageQuestions.length > 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="px-6">
                        <div className="space-y-6">
                          {stageQuestions.map((question) => {
                            // Determine decision type
                            let decisionType = 'Individual';
                            if (question.id === 'Q4') decisionType = 'Majority Vote';
                            if (question.id === 'Q6') decisionType = 'Consensus';
                            if (question.id === 'Q8') decisionType = 'Majority Vote';
                            if (question.id.startsWith('Q12') || question.id.startsWith('Q13'))
                              decisionType = 'Isolated';
                            if (question.id.includes('A') || question.id.includes('B'))
                              decisionType = 'Conditional';

                            // Skip text-only questions or render them differently
                            if (question.choices.length === 0) {
                              return (
                                <div key={question.id} className="border border-[#CED4DE] rounded-lg p-4 bg-[#E2E5EC]/50">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <HiOutlineQuestionMarkCircle className="text-xl text-[#12356B]" />
                                      <h4 className="text-base font-bold text-[#12356B]">{question.id}</h4>
                                    </div>
                                    <div className="px-3 py-1 rounded-full text-xs font-semibold bg-[#12356B]/10 text-[#12356B] border border-[#CED4DE]">
                                      Text Response
                                    </div>
                                  </div>
                                  <p className="text-sm text-slate-700 mb-2">{question.text}</p>
                                  {question.notes && (
                                    <div className="text-xs text-slate-600 italic mt-2">{question.notes}</div>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <QuestionCard
                                key={question.id}
                                questionNumber={question.id}
                                questionText={question.text}
                                decisionType={decisionType}
                                stageNumber={stageNum}
                                answers={question.choices.map((choice) => ({
                                  choice: choice.id,
                                  text: choice.text,
                                  scoring: {
                                    brain: choice.scores.brain,
                                    heart: choice.scores.heart,
                                    task: choice.scores.task,
                                    comm: choice.scores.comm,
                                  },
                                }))}
                                context={question.notes}
                                conditionalLogic={
                                  question.id.includes('A') || question.id.includes('B')
                                    ? question.notes
                                    : undefined
                                }
                              />
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </TabsContent>

          {/* SECTION 6: ENDINGS & OUTCOMES */}
          <TabsContent value="endings">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-[#12356B]">Endings & Outcomes</h2>
              <p className="text-slate-600 mb-6">
                All possible game endings, their conditions, and priority system. Higher priority endings are
                checked first.
              </p>

              {/* Ending Priority System */}
              <div className="bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm mb-6">
                <h3 className="text-lg font-bold text-[#12356B] mb-4">Ending Priority System</h3>
                <p className="text-sm text-slate-700 mb-4">
                  Endings are evaluated in priority order (highest first). Multiple endings can occur
                  simultaneously if their conditions are met. Catastrophic endings (ship destruction) take
                  precedence over success endings.
                </p>
                <div className="flex items-start gap-3 p-3 bg-[#DD6832]/10 border border-[#DD6832]/30 rounded-lg">
                  <HiOutlineExclamationCircle className="text-[#c4562a] text-xl mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-[#c4562a]">
                    <span className="font-bold">Important:</span> Ship destruction endings (priority 10) override
                    all other endings. Death conditions are evaluated separately before ending calculation.
                  </div>
                </div>
              </div>

              {/* Death Conditions */}
              <div className="bg-[#DD6832]/10 border-2 border-[#DD6832]/30 rounded-lg p-6 shadow-sm mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <FaSkullCrossbones className="text-3xl text-[#c4562a]" />
                  <h3 className="text-lg font-bold text-[#c4562a]">Death Conditions</h3>
                </div>
                <div className="space-y-3">
                  <div className="border-l-4 border-[#DD6832]/40 pl-4">
                    <div className="text-sm font-bold text-[#c4562a] mb-1">Energy Depletion</div>
                    <p className="text-sm text-[#c4562a]">
                      Player energy drops to 0%. Results in death. "In Memoriam" ending is triggered if any
                      player dies.
                    </p>
                  </div>
                  <div className="border-l-4 border-[#DD6832]/40 pl-4">
                    <div className="text-sm font-bold text-[#c4562a] mb-1">Insufficient Energy for Ascent</div>
                    <p className="text-sm text-[#c4562a]">
                      If a player doesn't have enough energy to swim to surface or cave in Stage 5B emergency
                      ascent. Typically requires 30%+ energy for full ascent.
                    </p>
                  </div>
                  <div className="border-l-4 border-[#DD6832]/40 pl-4">
                    <div className="text-sm font-bold text-[#c4562a] mb-1">
                      Fire Flower + Golden Octopus (Poisoning)
                    </div>
                    <p className="text-sm text-[#c4562a]">
                      If any player brings back both a Fire Flower (with Diamond Bugs) and a Golden Octopus,
                      the entire USS Follett crew is poisoned. Total party kill.
                    </p>
                  </div>
                  <div className="border-l-4 border-[#DD6832]/40 pl-4">
                    <div className="text-sm font-bold text-[#c4562a] mb-1">
                      Fire Flower + Rainbow Ray (Explosion)
                    </div>
                    <p className="text-sm text-[#c4562a]">
                      If any player brings back both a Fire Flower (with Diamond Bugs) and a Rainbow Ray, the
                      Diamond Bugs corrupt the Ray's DNA, causing space-time explosion. Total party kill.
                    </p>
                  </div>
                </div>
              </div>

              {/* All Endings */}
              <div>
                <h3 className="text-xl font-bold text-[#12356B] mb-4">All Possible Endings</h3>
                <div className="space-y-4">
                  {ENDINGS.sort((a, b) => b.priority - a.priority).map((ending) => {
                    // Determine ending type and colors
                    let colorClass = 'border-[#CED4DE] bg-[#E2E5EC]';
                    let iconColor = 'text-[#12356B]';
                    let icon = <FaTrophy />;

                    if (ending.id.includes('ship_')) {
                      colorClass = 'border-[#DD6832]/35 bg-[#DD6832]/10';
                      iconColor = 'text-[#c4562a]';
                      icon = <FaSkullCrossbones />;
                    } else if (ending.id.includes('memoriam')) {
                      colorClass = 'border-[#CED4DE] bg-[#E2E5EC]/50';
                      iconColor = 'text-slate-700';
                      icon = <FaSkullCrossbones />;
                    } else if (ending.id.includes('medal_of_shame') || ending.id.includes('permit_violation') || ending.id.includes('mission_failure')) {
                      colorClass = 'border-[#DD6832]/30 bg-[#DD6832]/10';
                      iconColor = 'text-[#c4562a]';
                      icon = <HiOutlineExclamationCircle />;
                    } else if (ending.id.includes('success') || ending.id.includes('continues') || ending.id.includes('honor')) {
                      colorClass = 'border-[#367A25]/25 bg-[#367A25]/10';
                      iconColor = 'text-[#367A25]';
                      icon = <HiOutlineCheckCircle />;
                    } else if (ending.id.includes('news') || ending.id.includes('journal') || ending.id.includes('historian')) {
                      colorClass = 'border-[#12356B]/30 bg-[#12356B]/10';
                      iconColor = 'text-[#12356B]';
                      icon = <FaNewspaper />;
                    }

                    return (
                      <div key={ending.id} className={`border-2 rounded-lg p-6 shadow-sm ${colorClass}`}>
                        <div className="flex items-start gap-4 mb-4">
                          <div className={`text-3xl ${iconColor} mt-1 flex-shrink-0`}>{icon}</div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="text-base font-bold text-[#12356B]">{ending.title}</h4>
                              <div className="px-2 py-1 bg-white border border-[#CED4DE] rounded text-xs font-semibold text-slate-700">
                                Priority: {ending.priority}
                              </div>
                            </div>
                            <div className="text-xs text-slate-600 italic mb-3">
                              <span className="font-semibold">Condition:</span> {ending.condition}
                            </div>
                            <p className="text-sm text-slate-800 leading-relaxed">{ending.text}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Conditional News Releases */}
              <div className="mt-8 bg-white border border-[#CED4DE] rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-bold text-[#12356B] mb-4">
                  <div className="flex items-center gap-2">
                    <FaNewspaper className="text-xl text-[#12356B]" />
                    <span>Conditional News Releases</span>
                  </div>
                </h3>
                <p className="text-sm text-slate-700 mb-4">
                  Some endings are only shown if specific conditions are met. These are "news releases" that
                  supplement the main ending.
                </p>
                <div className="space-y-3">
                  <div className="border-l-4 border-[#12356B]/40 pl-4 py-2">
                    <div className="text-sm font-bold text-[#12356B] mb-1">GEE's Triumphant Return</div>
                    <p className="text-xs text-slate-700">
                      Always shown (priority 1). GEE returns with sea cigars.
                    </p>
                  </div>
                  <div className="border-l-4 border-[#12356B]/40 pl-4 py-2">
                    <div className="text-sm font-bold text-[#12356B] mb-1">GEE's Journal Discovery</div>
                    <p className="text-xs text-slate-700">
                      If GEE has Ahab's Platinum Journal (priority 2). Qualifies him for Grand Poobah Award.
                    </p>
                  </div>
                  <div className="border-l-4 border-[#12356B]/40 pl-4 py-2">
                    <div className="text-sm font-bold text-[#12356B] mb-1">Golden Octopus Captured</div>
                    <p className="text-xs text-slate-700">
                      If any player has Golden Octopus (priority 5). Player promoted to Galactic Expedition
                      Expert.
                    </p>
                  </div>
                  <div className="border-l-4 border-[#12356B]/40 pl-4 py-2">
                    <div className="text-sm font-bold text-[#12356B] mb-1">New Galactic Historian Named</div>
                    <p className="text-xs text-slate-700">
                      If any player (not GEE) has Platinum Journal (priority 8). Player becomes Galactic
                      Historian with 10-year tour.
                    </p>
                  </div>
                  <div className="border-l-4 border-[#12356B]/40 pl-4 py-2">
                    <div className="text-sm font-bold text-[#12356B] mb-1">Articus Crash Ruled Accidental</div>
                    <p className="text-xs text-slate-700">
                      If any player has Black Box (priority 7). Absolves Articus I crew of responsibility.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
